import React, { useState, useEffect, useCallback } from 'react';
import { EnrollmentApi } from '../../services/api/EnrollmentApi';
import { ProgressApi } from '../../services/api/ProgressApi';
import CourseInvitePanel from './CourseInvitePanel';
import './CourseManageTab.css';

/**
 * CourseManageTab — Owner/professor dashboard tab for shared courses.
 * Shows invite management, student roster, and analytics.
 */
export default function CourseManageTab({ endeavorId }) {
  const [activeSection, setActiveSection] = useState('invites');
  const [rosterRefreshToken, setRosterRefreshToken] = useState(0);

  return (
    <div className="course-manage-tab">
      <div className="course-manage-nav">
        <button
          className={`course-manage-nav-btn ${activeSection === 'invites' ? 'active' : ''}`}
          onClick={() => setActiveSection('invites')}
        >
          Invitations
        </button>
        <button
          className={`course-manage-nav-btn ${activeSection === 'roster' ? 'active' : ''}`}
          onClick={() => setActiveSection('roster')}
        >
          Students
        </button>
        <button
          className={`course-manage-nav-btn ${activeSection === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveSection('analytics')}
        >
          Analytics
        </button>
      </div>

      {activeSection === 'invites' && (
        <CourseInvitePanel
          endeavorId={endeavorId}
          onRosterChanged={() => setRosterRefreshToken(t => t + 1)}
        />
      )}
      {activeSection === 'roster' && (
        <RosterSection endeavorId={endeavorId} refreshToken={rosterRefreshToken} />
      )}
      {activeSection === 'analytics' && <AnalyticsSection endeavorId={endeavorId} />}
    </div>
  );
}

/* ── Student Roster Section ── */
function RosterSection({ endeavorId, refreshToken }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRoster = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await EnrollmentApi.getAllEnrollments(endeavorId);
      if (resp.ok) {
        const data = await resp.json();
        setEnrollments(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('Failed to load roster', err);
    } finally {
      setLoading(false);
    }
  }, [endeavorId]);

  useEffect(() => { loadRoster(); }, [loadRoster, refreshToken]);

  const handleRemove = async (userId) => {
    try {
      const resp = await EnrollmentApi.unenrollStudent(endeavorId, userId);
      if (resp.ok) await loadRoster();
    } catch (err) {
      console.warn('Failed to remove student', err);
    }
  };

  return (
    <div className="course-manage-section">
      <h3>Student Roster</h3>
      <p className="course-manage-hint">{enrollments.length} enrolled</p>

      {loading ? (
        <p className="course-manage-loading">Loading roster...</p>
      ) : enrollments.length === 0 ? (
        <p className="course-manage-empty">No students enrolled yet.</p>
      ) : (
        <table className="roster-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Enrolled</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {enrollments.map(e => {
              const userId = e.userId || e.user_id;
              const isOwner = e.role === 'owner';
              return (
                <tr key={e.enrollmentId || e.id}>
                  <td>{e.userName || e.name || '—'}</td>
                  <td>{e.userEmail || e.email || '—'}</td>
                  <td><span className={`roster-role roster-role--${e.role}`}>{e.role}</span></td>
                  <td>{e.status}</td>
                  <td>{e.enrolledAt ? new Date(e.enrolledAt).toLocaleDateString() : '—'}</td>
                  <td>
                    {!isOwner && (
                      <button className="btn btn-sm btn-danger" onClick={() => handleRemove(userId)}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ── Analytics Section ── */
function AnalyticsSection({ endeavorId }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const resp = await ProgressApi.getEndeavorAnalytics(endeavorId);
        if (resp.ok) {
          setAnalytics(await resp.json());
        }
      } catch (err) {
        console.warn('Failed to load analytics', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [endeavorId]);

  if (loading) return <p className="course-manage-loading">Loading analytics...</p>;
  if (!analytics) return <p className="course-manage-empty">No analytics data available.</p>;

  const assignmentProgress = analytics.assignmentProgress || analytics.assignments || [];
  const eventProgress = analytics.eventProgress || analytics.events || [];

  return (
    <div className="course-manage-section">
      <h3>Course Analytics</h3>

      {assignmentProgress.length > 0 && (
        <div className="analytics-block">
          <h4>Assignment Completion</h4>
          <div className="analytics-bars">
            {assignmentProgress.map((a, i) => {
              const total = a.totalStudents || a.total || 1;
              const completed = a.completedCount || a.completed || 0;
              const pct = Math.round((completed / total) * 100);
              return (
                <div key={i} className="analytics-bar-row">
                  <span className="analytics-bar-label" title={a.assignmentName || a.name}>
                    {a.assignmentName || a.name || `Assignment ${i + 1}`}
                  </span>
                  <div className="analytics-bar-track">
                    <div className="analytics-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="analytics-bar-pct">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {eventProgress.length > 0 && (
        <div className="analytics-block">
          <h4>Event Completion</h4>
          <div className="analytics-bars">
            {eventProgress.map((e, i) => {
              const total = e.totalStudents || e.total || 1;
              const completed = e.completedCount || e.completed || 0;
              const pct = Math.round((completed / total) * 100);
              return (
                <div key={i} className="analytics-bar-row">
                  <span className="analytics-bar-label" title={e.eventName || e.name}>
                    {e.eventName || e.name || `Event ${i + 1}`}
                  </span>
                  <div className="analytics-bar-track">
                    <div className="analytics-bar-fill analytics-bar-fill--event" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="analytics-bar-pct">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {assignmentProgress.length === 0 && eventProgress.length === 0 && (
        <p className="course-manage-empty">No progress data yet. Students will appear here once they start completing activities.</p>
      )}
    </div>
  );
}
